import axios from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
  clearSessionStorage,
  isAuthEndpoint,
} from './auth';
import { isAccessTokenExpired } from './tokenClaims';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://soe5fvpfk1.execute-api.ap-south-1.amazonaws.com/production';
  // 'http://localhost:8000';
const api = axios.create({
  baseURL: API_BASE_URL,
});

export async function logout() {
  const refreshToken = getRefreshToken();
  clearSessionStorage();
  try {
    if (refreshToken) {
      await api.post('/user/logout', { refreshToken });
    }
  } catch {
    /* proceed with local cleanup even if logout API fails */
  }
}

let isRefreshing = false;
let refreshQueue = [];

function processRefreshQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

function redirectToLogin() {
  clearSessionStorage();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function performTokenRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const refreshResponse = await api.post('/user/refresh-token', {
      refreshToken,
    });
    const newToken =
      refreshResponse.data?.accessToken || refreshResponse.data?.token || '';
    if (!newToken) {
      throw new Error('Refresh response missing access token');
    }
    updateAccessToken(newToken);
    processRefreshQueue(null, newToken);
    return newToken;
  } catch (refreshError) {
    processRefreshQueue(refreshError, null);
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

async function ensureValidAccessToken() {
  const accessToken = getAccessToken();
  if (accessToken && !isAccessTokenExpired(accessToken)) {
    return accessToken;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    if (accessToken) return accessToken;
    throw new Error('Not authenticated');
  }

  return performTokenRefresh();
}

/** Refresh access token using the stored refresh token (e.g. on app init). */
export async function refreshSessionTokens() {
  return performTokenRefresh();
}

api.interceptors.request.use(
  async (config) => {
    const url = config.url ?? '';
    if (isAuthEndpoint(url)) {
      return config;
    }

    const onLoginPage =
      typeof window !== 'undefined' && window.location.pathname === '/login';
    if (onLoginPage) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    try {
      const token = await ensureValidAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';
    const status = error.response?.status;

    const onLoginPage =
      typeof window !== 'undefined' && window.location.pathname === '/login';

    if (status === 403 && !isAuthEndpoint(url)) {
      error.isAccessDenied = true;
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      !isAuthEndpoint(url) &&
      !onLoginPage &&
      originalRequest &&
      !originalRequest._retry
    ) {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const newToken = await performTokenRefresh();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

export const createSOP = (data) => api.post('/sop/create', data);
export const getSOPTypes = () => api.get('/sop/types');
export const getSOPByType = (type) => api.get(`/sop/${type}`);
export const submitEmployeeSOP = (data) => api.post('/sop/employee/submit', data);
